import { useEffect, useState } from "react";
import { api, ApiError, type AprovacaoLaudo, type HistoricoItem, type LaudoCompleto } from "../api";
import { formatDate } from "../format";
import { SECOES_LAUDO, paresDaSecao } from "../laudo-campos";
import { IconClose, IconDownload, IconFlask, IconCheck, IconAlert } from "../icons";

/**
 * O LAUDO INTEIRO NA TELA — dois cliques na linha da aba Relatórios.
 *
 * Antes daqui, a única forma de ver o que tinha sido analisado era BAIXAR o
 * .docx e abrir no Word: ninguém confere um valor de rigidez dielétrica assim.
 * Esta tela mostra o mesmo conteúdo do laudo, na ordem do papel:
 *
 *   cabeçalho (OS, cliente, equipamento, datas e prazos) → identificação do
 *   transformador → as seções de ensaio que aquela OS pediu → parecer →
 *   conferência do laudo (quem pediu, quem aprovou, quando).
 *
 * REGRAS:
 *  - só LEITURA: quem precisa mudar valor volta pela aba Análises;
 *  - seção NÃO SOLICITADA na OS não é escondida à toa — ela aparece marcada
 *    como "não solicitada", porque sumir com uma seção faria parecer que o
 *    laboratório esqueceu de fazer o ensaio;
 *  - campo em branco fica com "—" à vista: no laudo, o que NÃO foi medido é
 *    informação tanto quanto o que foi.
 */

const RESULTADO_PILL: Record<string, string> = {
  Aprovado: "pill-ok",
  Reprovado: "pill-danger",
  "Com ressalvas": "pill-warn",
};

const APROVACAO_PILL: Record<string, string> = {
  Aprovado: "pill-ok",
  Reprovado: "pill-danger",
  Pendente: "pill-warn",
};

/**
 * "2026-08-20 11:18:21.82-03" → "20/08/2026 às 11:18".
 *
 * O Postgres devolve a data com espaço no lugar do "T" e o fuso em duas casas
 * ("-03"). Nem todo navegador aceita esse formato no `new Date`, então ele é
 * normalizado aqui antes — data errada num laudo não é detalhe.
 */
function dataHora(valor: string | null | undefined): string {
  if (!valor) return "—";
  const iso = String(valor).replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return formatDate(valor);
  return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function tamanho(bytes: number | null | undefined): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function LaudoDetalhe({
  item,
  onCancel,
  onBaixar,
}: {
  item: HistoricoItem;
  onCancel: () => void;
  onBaixar: (item: HistoricoItem) => void;
}) {
  const [analise, setAnalise] = useState<LaudoCompleto | null>(null);
  const [aprovacao, setAprovacao] = useState<AprovacaoLaudo | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    api.historico
      .obter(item.id)
      .then((r) => {
        if (!vivo) return;
        setAnalise(r.analise);
        setAprovacao(r.aprovacao);
      })
      .catch((e) => vivo && setErro(e instanceof ApiError ? e.message : "Erro ao abrir o laudo."))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [item.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  /** Seções que a OS pediu; lista vazia/nula = todas (é como as antigas ficaram). */
  const pedidas = analise?.analises_selecionadas;
  const foiPedida = (chave?: string) =>
    !chave || !Array.isArray(pedidas) || pedidas.length === 0 || pedidas.includes(chave);

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card modal-wide laudo-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head rf-head">
          <div className="rf-head-txt">
            <span className="rf-head-icon">
              <IconFlask size={20} />
            </span>
            <div>
              <h3>Laudo da OS {item.os_numero}</h3>
              <p className="rf-head-sub">
                {item.cliente || "cliente não informado"}
                {item.equipamento ? ` · ${item.equipamento}` : ""} · despachado em{" "}
                {formatDate(item.despachado_em)}
              </p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onCancel} title="Fechar (Esc)">
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body laudo-corpo">
          {carregando && <div className="empty">Abrindo o laudo…</div>}
          {erro && <div className="form-error">{erro}</div>}

          {analise && !carregando && (
            <>
              <section className="laudo-secao">
                <header className="rf-secao-head">
                  <h4>Ordem de serviço</h4>
                  {analise.resultado && (
                    <span
                      className={`pill ${RESULTADO_PILL[analise.resultado] ?? "pill-neutral"} laudo-pill`}
                    >
                      {analise.resultado}
                    </span>
                  )}
                </header>
                <dl className="laudo-grid">
                  <Par rotulo="OS" valor={analise.os_numero} destaque />
                  <Par rotulo="Cliente" valor={analise.cliente} />
                  <Par rotulo="Equipamento" valor={analise.equipamento} />
                  <Par rotulo="Solicitante" valor={analise.solicitante} />
                  <Par rotulo="Responsável pelo laudo" valor={analise.responsavel} />
                  <Par rotulo="Situação" valor={analise.status} />
                  <Par rotulo="Recebida em" valor={dataHora(analise.data_recebimento)} />
                  <Par rotulo="Despachada em" valor={dataHora(analise.despachado_em)} />
                  <Par rotulo="Prazo de coleta" valor={formatDate(analise.prazo_coleta)} />
                  <Par rotulo="Prazo de retorno" valor={formatDate(analise.prazo_retorno)} />
                </dl>
              </section>

              {SECOES_LAUDO.map((secao) => {
                const pedida = foiPedida(secao.selecao);
                const pares = paresDaSecao(analise[secao.dados], secao.campos);
                const preenchidos = pares.filter((p) => p.valor !== "").length;
                return (
                  <section
                    className={`laudo-secao${pedida ? "" : " nao-pedida"}`}
                    key={secao.dados}
                  >
                    <header className="rf-secao-head">
                      <h4>{secao.titulo}</h4>
                      {!pedida ? (
                        <span className="rf-secao-hint">não solicitada nesta OS</span>
                      ) : (
                        <span className="rf-secao-hint">
                          {preenchidos} de {pares.length} preenchido(s)
                        </span>
                      )}
                    </header>
                    {preenchidos === 0 ? (
                      <p className="laudo-vazio">
                        {pedida
                          ? "Nenhum valor preenchido nesta seção."
                          : "Seção não solicitada — nada foi ensaiado aqui."}
                      </p>
                    ) : (
                      <dl className="laudo-grid">
                        {pares.map((p) => (
                          <Par key={p.label} rotulo={p.label} valor={p.valor} />
                        ))}
                      </dl>
                    )}
                  </section>
                );
              })}

              <section className="laudo-secao">
                <header className="rf-secao-head">
                  <h4>Parecer do laboratório</h4>
                </header>
                {analise.parecer?.trim() ? (
                  <p className="laudo-parecer">{analise.parecer}</p>
                ) : (
                  <p className="laudo-vazio">Sem parecer escrito.</p>
                )}
              </section>

              <section className="laudo-secao">
                <header className="rf-secao-head">
                  <h4>Conferência do laudo</h4>
                  {aprovacao && (
                    <span
                      className={`pill ${APROVACAO_PILL[aprovacao.status] ?? "pill-neutral"} laudo-pill`}
                    >
                      {aprovacao.status}
                    </span>
                  )}
                </header>
                {aprovacao ? (
                  <>
                    <dl className="laudo-grid">
                      <Par rotulo="Solicitada por" valor={aprovacao.solicitado_por} />
                      <Par rotulo="Solicitada em" valor={dataHora(aprovacao.solicitado_em)} />
                      <Par rotulo="Avaliada por" valor={aprovacao.avaliado_por} />
                      <Par rotulo="Avaliada em" valor={dataHora(aprovacao.avaliado_em)} />
                    </dl>
                    {aprovacao.motivo?.trim() && (
                      <p className="laudo-motivo">
                        <IconAlert size={13} /> {aprovacao.motivo}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="laudo-vazio">
                    Este laudo foi despachado sem passar pela aba “Aprovação de relatório”.
                  </p>
                )}
              </section>

              <section className="laudo-secao">
                <header className="rf-secao-head">
                  <h4>Arquivo do laudo</h4>
                </header>
                {analise.tem_laudo ? (
                  <p className="laudo-arquivo">
                    <IconCheck size={14} />
                    <span>
                      {analise.laudo_nome || "laudo.docx"}
                      {tamanho(analise.laudo_bytes) ? ` · ${tamanho(analise.laudo_bytes)}` : ""}
                    </span>
                  </p>
                ) : (
                  <p className="laudo-vazio">
                    Sem arquivo gerado — esta análise foi despachada sem o .docx.
                  </p>
                )}
              </section>
            </>
          )}
        </div>

        <div className="modal-foot">
          <span className="rf-atalho">Esc fecha</span>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Fechar
          </button>
          {/* Mesma folha da tela, sem depender do Word: a pessoa que só quer
              conferir (ou mandar por e-mail) resolve com Ctrl+P → PDF. */}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => analise && imprimir(analise, aprovacao)}
            disabled={!analise}
          >
            Imprimir / PDF
          </button>
          {item.tem_laudo && (
            <button type="button" className="btn-primary" onClick={() => onBaixar(item)}>
              <IconDownload size={15} /> Baixar o laudo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Escapa o texto que vai para a folha de impressão (é HTML montado à mão). */
function esc(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * A MESMA folha da tela, pronta para o Ctrl+P (ou "salvar em PDF").
 *
 * Não substitui o .docx do laudo — serve para quem só precisa conferir ou
 * mandar por e-mail sem ter o Word na máquina.
 */
function imprimir(analise: LaudoCompleto, aprovacao: AprovacaoLaudo | null) {
  const pedidas = analise.analises_selecionadas;
  const foiPedida = (chave?: string) =>
    !chave || !Array.isArray(pedidas) || pedidas.length === 0 || pedidas.includes(chave);

  const bloco = (titulo: string, pares: { label: string; valor: string }[], nota = "") =>
    `<h2>${esc(titulo)}${nota ? ` <small>${esc(nota)}</small>` : ""}</h2>` +
    (pares.some((p) => p.valor !== "")
      ? `<table><tbody>${pares
          .map(
            (p) =>
              `<tr><th>${esc(p.label)}</th><td>${p.valor === "" ? "—" : esc(p.valor)}</td></tr>`,
          )
          .join("")}</tbody></table>`
      : `<p class="vazio">Nada preenchido nesta seção.</p>`);

  const cabecalho = [
    { label: "OS", valor: analise.os_numero ?? "" },
    { label: "Cliente", valor: analise.cliente ?? "" },
    { label: "Equipamento", valor: analise.equipamento ?? "" },
    { label: "Solicitante", valor: analise.solicitante ?? "" },
    { label: "Responsável pelo laudo", valor: analise.responsavel ?? "" },
    { label: "Resultado", valor: analise.resultado ?? "" },
    { label: "Recebida em", valor: dataHora(analise.data_recebimento) },
    { label: "Despachada em", valor: dataHora(analise.despachado_em) },
  ];

  const secoes = SECOES_LAUDO.map((s) =>
    bloco(
      s.titulo,
      paresDaSecao(analise[s.dados], s.campos),
      foiPedida(s.selecao) ? "" : "(não solicitada nesta OS)",
    ),
  ).join("");

  const conferencia = aprovacao
    ? bloco("Conferência do laudo", [
        { label: "Situação", valor: aprovacao.status ?? "" },
        { label: "Solicitada por", valor: aprovacao.solicitado_por ?? "" },
        { label: "Solicitada em", valor: dataHora(aprovacao.solicitado_em) },
        { label: "Avaliada por", valor: aprovacao.avaliado_por ?? "" },
        { label: "Avaliada em", valor: dataHora(aprovacao.avaliado_em) },
        { label: "Motivo", valor: aprovacao.motivo ?? "" },
      ])
    : `<h2>Conferência do laudo</h2><p class="vazio">Despachado sem passar pela aprovação.</p>`;

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
    <title>Laudo OS ${esc(analise.os_numero)} — ${esc(analise.cliente ?? "")}</title>
    <style>
      body{font-family:system-ui,Segoe UI,sans-serif;color:#13241b;margin:26px;font-size:12.5px}
      h1{font-size:18px;margin:0 0 2px}
      .cab{border-bottom:2px solid #0c7a3f;padding-bottom:10px;margin-bottom:14px}
      .cod{font-size:11px;color:#5e7268}
      h2{font-size:12px;text-transform:uppercase;letter-spacing:.4px;margin:16px 0 6px;
         border-bottom:1px solid #cbd8d0;padding-bottom:4px}
      h2 small{text-transform:none;letter-spacing:0;color:#8a9a91;font-weight:400}
      table{width:100%;border-collapse:collapse;margin:0}
      th,td{border:1px solid #cbd8d0;padding:4px 7px;text-align:left;vertical-align:top}
      th{background:#eaf5ee;width:38%;font-weight:600;font-size:11.5px}
      .parecer{border:1px solid #cbd8d0;border-radius:6px;padding:8px 10px;white-space:pre-wrap}
      .vazio{color:#5e7268;margin:0}
      .ass{display:flex;gap:36px;margin-top:40px}
      .ass div{flex:1;border-top:1px solid #333;padding-top:5px;font-size:11px;text-align:center}
      @media print{body{margin:10mm}}
    </style></head><body>
    <div class="cab">
      <h1>Análise de Óleo — OS ${esc(analise.os_numero)}</h1>
      <span class="cod">Laboratório SERCAMP · impresso em ${new Date().toLocaleString("pt-BR")}</span>
    </div>
    ${bloco("Ordem de serviço", cabecalho)}
    ${secoes}
    <h2>Parecer do laboratório</h2>
    ${
      analise.parecer?.trim()
        ? `<p class="parecer">${esc(analise.parecer)}</p>`
        : `<p class="vazio">Sem parecer escrito.</p>`
    }
    ${conferencia}
    <div class="ass"><div>Responsável pelo laudo</div><div>Conferência</div></div>
    </body></html>`;

  const janela = window.open("", "_blank");
  if (!janela) {
    alert("O navegador bloqueou a janela de impressão. Libere os pop-ups para este site.");
    return;
  }
  janela.document.write(html);
  janela.document.close();
  janela.focus();
  janela.print();
}

/** Uma linha "rótulo → valor" do laudo. Vazio vira "—", nunca some da tela. */
function Par({
  rotulo,
  valor,
  destaque,
}: {
  rotulo: string;
  valor: string | null | undefined;
  destaque?: boolean;
}) {
  const texto = String(valor ?? "").trim();
  return (
    <div className={`laudo-par${texto === "" ? " vazio" : ""}${destaque ? " destaque" : ""}`}>
      <dt>{rotulo}</dt>
      <dd>{texto === "" ? "—" : texto}</dd>
    </div>
  );
}
